import { useCallback, useEffect, useMemo, useRef, useState, Fragment } from "react";
import { Link } from "react-router-dom";
import { api, extractError, TOKEN_KEY } from "../lib/api";
import { Button } from "../components/ui/button";
import { Card, CardContent } from "../components/ui/card";
import { Alert, AlertDescription } from "../components/ui/alert";
import { Checkbox } from "../components/ui/checkbox";
import { Input } from "../components/ui/input";
import { Label } from "../components/ui/label";
import { Textarea } from "../components/ui/textarea";
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter, DialogDescription,
} from "../components/ui/dialog";
import {
  Download, Loader2, ExternalLink, Building2, CheckCircle2, RotateCcw, Banknote,
  FileSearch, ChevronDown, ChevronUp, ChevronRight, FileText, AlertTriangle,
  Truck, Container, User as UserIcon,
} from "lucide-react";
import TmsStatusBadge from "./TmsStatusBadge";

const API_BASE = process.env.REACT_APP_BACKEND_URL + "/api";

// Mirror of DriverCharges STATUS_TABS (key/label/filter shape). Excludes
// `sent_to_tms` (no TMS) and `not_applicable` (workflow doesn't apply).
const STATUS_TABS = [
  { key: "all", label: "All", filter: "pending,charged,failed" },
  { key: "pending", label: "Pending", filter: "pending" },
  { key: "charged", label: "Charged", filter: "charged" },
  { key: "failed", label: "Failed", filter: "failed" },
];

function fmtMoney(cents) {
  return `$${((cents || 0) / 100).toFixed(2)}`;
}
function fmtDate(iso) {
  if (!iso) return "—";
  try { return new Date(iso).toLocaleDateString(); } catch { return "—"; }
}
function fmtDateTime(iso) {
  if (!iso) return "—";
  try { return new Date(iso).toLocaleString(); } catch { return "—"; }
}
function fmtSize(bytes) {
  if (!bytes) return "";
  if (bytes >= 1024 * 1024) return `${(bytes / (1024 * 1024)).toFixed(1)} MB`;
  return `${(bytes / 1024).toFixed(1)} KB`;
}

const STATUS_PILL = {
  pending: "bg-amber-100 text-amber-800",
  charged: "bg-emerald-100 text-emerald-800",
  failed: "bg-rose-100 text-rose-800",
  not_applicable: "bg-slate-100 text-slate-500",
};

function StatusPill({ status }) {
  const s = status || "pending";
  return (
    <span className={`inline-flex rounded-full px-2 py-0.5 text-xs font-medium ${STATUS_PILL[s] || STATUS_PILL.pending}`}>
      {s.replace(/_/g, " ")}
    </span>
  );
}

function EquipmentChips({ rows }) {
  if (!rows?.length) return <span className="text-xs text-slate-400">—</span>;
  return (
    <div className="flex flex-wrap gap-1">
      {rows.map((r) => (
        <span key={r.id} className="inline-flex items-center gap-1 rounded-md bg-slate-100 px-1.5 py-0.5 text-xs text-slate-700"
          data-testid={`lc-equipment-chip-${r.id}`}>
          {r.label || r.id}
          <TmsStatusBadge status={r.status} testId={`lc-equipment-status-${r.id}`} />
        </span>
      ))}
    </div>
  );
}

/**
 * LesseeChargesCard — accounting/admin view of expenses marked
 * `charge_type == "lessee"`. Row actions mirror driver-charges MINUS Send-to-TMS.
 */
export default function LesseeChargesCard() {
  const [items, setItems] = useState([]);
  const [total, setTotal] = useState(0);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState("");
  const [exporting, setExporting] = useState(false);
  // Tab state — default "pending" matches DriverCharges.jsx line 128.
  const [tab, setTab] = useState("pending");

  // Audit panel state (mirrors DriverCharges audit panel).
  const [auditOpen, setAuditOpen] = useState(false);
  const [auditDays, setAuditDays] = useState(30);
  const [auditRows, setAuditRows] = useState([]);
  const [auditLoading, setAuditLoading] = useState(false);
  const [auditError, setAuditError] = useState("");

  // Mark dialog state (single + bulk)
  const [markOpen, setMarkOpen] = useState(false);
  const [markTarget, setMarkTarget] = useState(null); // expense id OR "bulk"
  const [markForm, setMarkForm] = useState({ external_ref: "", note: "" });
  const [busy, setBusy] = useState(false);

  // Bulk selection
  const [selected, setSelected] = useState(new Set());

  // Phase 3 — expand state per group_key, and independent sub-row selection
  // keyed by allocation id.  `selected` covers whole-group + flat rows;
  // `subSelected` covers per-charge subset selection inside expanded groups.
  // These two sets never overlap for the same underlying allocation id
  // (see `flattenSelectedIds`).
  const [expanded, setExpanded] = useState(new Set());
  const [subSelected, setSubSelected] = useState(new Set());
  // Per-group receipt-retry busy flag (avoids double-clicks on the
  // warning icon).  Keyed by group_key.
  const [retryingReceipt, setRetryingReceipt] = useState(new Set());

  // Grouped-row details modal state.  `detailsGroup` mirrors the
  // response shape of GET /lessee-charges/groups/{group_key} — see
  // backend/routers/lessee_charges.py::get_group_details.  Polling only
  // fires while the modal is open AND the receipt PDF is still
  // rendering; the effect below tears itself down on close.
  const [detailsOpen, setDetailsOpen] = useState(false);
  const [detailsKey, setDetailsKey] = useState(null);
  const [detailsGroup, setDetailsGroup] = useState(null);
  const [detailsLoading, setDetailsLoading] = useState(false);
  const [detailsError, setDetailsError] = useState("");
  const [detailsLineItemsOpen, setDetailsLineItemsOpen] = useState(false);

  // Blob-URL loader for the details-modal receipt card.  The auth-gated
  // /api/receipts/{id} route can't be hit via a plain <a target="_blank">
  // — the new tab has no Authorization header and 401s.  Match the toll
  // pattern in ExpenseDetail.jsx: authenticated fetch → Blob →
  // URL.createObjectURL → open the blob URL in a new tab (same-origin,
  // no auth needed).  `blobRef` tracks the currently-live URL so a
  // close/receipt-id change can revoke without depending on state
  // inside the load effect.
  const [receiptBlob, setReceiptBlob] = useState({
    id: null, url: null, error: "", loading: false,
  });
  const blobRef = useRef(null);

  const fetchReceiptBlob = useCallback(async (rid) => {
    if (blobRef.current) {
      URL.revokeObjectURL(blobRef.current);
      blobRef.current = null;
    }
    setReceiptBlob({ id: rid, url: null, error: "", loading: true });
    try {
      // Copied verbatim from ExpenseDetail.jsx (lines 68-82) — SAME
      // token key, same header shape, same blob path.
      const token = localStorage.getItem(TOKEN_KEY);
      const res = await fetch(`${API_BASE}/receipts/${rid}`, {
        headers: token ? { Authorization: `Bearer ${token}` } : {},
      });
      if (!res.ok) throw new Error(`Failed to load PDF (HTTP ${res.status})`);
      const blob = await res.blob();
      const url = URL.createObjectURL(blob);
      blobRef.current = url;
      setReceiptBlob({ id: rid, url, error: "", loading: false });
    } catch (e) {
      setReceiptBlob({
        id: rid, url: null,
        error: e.message || "Failed to load PDF — try Retry",
        loading: false,
      });
    }
  }, []);

  const retryReceiptBlob = useCallback(() => {
    const rid = detailsGroup?.receipt?.id;
    if (rid) fetchReceiptBlob(rid);
  }, [detailsGroup?.receipt?.id, fetchReceiptBlob]);

  const filter = useMemo(
    () => STATUS_TABS.find((t) => t.key === tab)?.filter || "pending",
    [tab],
  );

  const load = useCallback(async () => {
    setLoading(true); setError("");
    try {
      const r = await api.get("/lessee-charges", { params: { status: filter } });
      setItems(r.data?.items || []);
      setTotal(r.data?.total || 0);
      setSelected(new Set());
    } catch (e) { setError(extractError(e)); }
    finally { setLoading(false); }
  }, [filter]);

  useEffect(() => { load(); }, [load]);

  // Audit panel loader — mirrors DriverCharges loadAudit().
  const loadAudit = useCallback(async () => {
    setAuditLoading(true); setAuditError("");
    try {
      const r = await api.get("/lessee-charges/audit", { params: { days: auditDays } });
      setAuditRows(r.data?.rows || []);
    } catch (e) { setAuditError(extractError(e)); }
    finally { setAuditLoading(false); }
  }, [auditDays]);

  useEffect(() => { if (auditOpen) loadAudit(); }, [auditOpen, loadAudit]);

  const allEligibleIds = useMemo(
    () => items.filter((it) => (it.lessee_charge_status || "pending") === "pending").map((it) => it.id),
    [items],
  );

  const toggleRow = (id) =>
    setSelected((prev) => {
      const next = new Set(prev);
      if (next.has(id)) next.delete(id); else next.add(id);
      return next;
    });

  const selectAllOnPage = () => {
    setSelected((prev) =>
      prev.size === allEligibleIds.length ? new Set() : new Set(allEligibleIds),
    );
  };

  // Phase 3 — flatten `selected` (group_keys and flat expense ids) plus
  // `subSelected` (per-charge picks inside expanded groups) into the
  // single `ids: [...]` payload the bulk-mark endpoint accepts.  A
  // selected group_key contributes its full `charge_ids`; a sub-row
  // whose parent group is ALSO in `selected` is skipped (parent covers it).
  const flattenSelectedIds = useCallback(() => {
    const out = new Set();
    const parentGroupsSelected = new Set();
    // Group-selected rows contribute all their charge_ids.
    for (const it of items) {
      if (!it.is_group) continue;
      if (selected.has(it.group_key)) {
        parentGroupsSelected.add(it.group_key);
        for (const cid of (it.charge_ids || [])) out.add(cid);
      }
    }
    // Flat rows selected by their own id.
    for (const it of items) {
      if (it.is_group) continue;
      if (selected.has(it.id)) out.add(it.id);
    }
    // Sub-row picks — skip anything already covered by a selected parent.
    for (const it of items) {
      if (!it.is_group || parentGroupsSelected.has(it.group_key)) continue;
      for (const c of (it.charges || [])) {
        if (subSelected.has(c.id)) out.add(c.id);
      }
    }
    return [...out];
  }, [items, selected, subSelected]);

  // Retry a failed receipt render.  Backend clears backoff + re-enqueues.
  const retryReceipt = async (groupKey) => {
    setRetryingReceipt((prev) => new Set(prev).add(groupKey));
    try {
      await api.post(`/lessee-charges/groups/${encodeURIComponent(groupKey)}/receipt/retry`);
      await load();
    } catch (e) { setError(extractError(e)); }
    finally {
      setRetryingReceipt((prev) => {
        const next = new Set(prev); next.delete(groupKey); return next;
      });
    }
  };

  // Fetch the aggregated details payload for a group + open the modal.
  const fetchGroupDetails = useCallback(async (groupKey) => {
    try {
      const r = await api.get(
        `/lessee-charges/groups/${encodeURIComponent(groupKey)}`,
      );
      setDetailsGroup(r.data || null);
      setDetailsError("");
    } catch (e) {
      setDetailsError(extractError(e));
    }
  }, []);

  const openGroupDetails = async (groupKey) => {
    setDetailsKey(groupKey);
    setDetailsGroup(null);
    setDetailsError("");
    setDetailsLineItemsOpen(false);
    setDetailsLoading(true);
    setDetailsOpen(true);
    await fetchGroupDetails(groupKey);
    setDetailsLoading(false);
  };

  const retryDetailsReceipt = async () => {
    if (!detailsKey) return;
    await retryReceipt(detailsKey);
    await fetchGroupDetails(detailsKey);
  };

  // Poll while the details modal is open and the receipt is still
  // rendering — mirrors the list-page 15s poll cadence.  Tear-down on
  // modal close is automatic via the effect cleanup.
  useEffect(() => {
    if (!detailsOpen || !detailsKey) return undefined;
    if (!detailsGroup?.receipt_pending) return undefined;
    const t = setInterval(() => { fetchGroupDetails(detailsKey); }, 3000);
    return () => clearInterval(t);
  }, [detailsOpen, detailsKey, detailsGroup?.receipt_pending, fetchGroupDetails]);

  // Blob load / revoke lifecycle.  Fires on:
  //  * dialog open + receipt.id present → authenticated fetch → blob URL
  //  * receipt.id changing between polls (invalidation → fresh render) →
  //    revoke stale blob, fetch new
  //  * dialog close OR receipt.id going null (server render errored /
  //    still pending) → revoke + reset
  // Component unmount is covered by the second effect below.
  const currentReceiptId = detailsOpen ? (detailsGroup?.receipt?.id || null) : null;
  useEffect(() => {
    if (!currentReceiptId) {
      if (blobRef.current) {
        URL.revokeObjectURL(blobRef.current);
        blobRef.current = null;
      }
      setReceiptBlob({ id: null, url: null, error: "", loading: false });
      return;
    }
    // Already loaded (or currently loading / errored) this exact id →
    // no refetch.  Retry goes through `retryReceiptBlob` explicitly.
    if (
      receiptBlob.id === currentReceiptId
      && (receiptBlob.url || receiptBlob.loading || receiptBlob.error)
    ) return;
    fetchReceiptBlob(currentReceiptId);
  }, [
    currentReceiptId,
    receiptBlob.id, receiptBlob.url, receiptBlob.loading, receiptBlob.error,
    fetchReceiptBlob,
  ]);

  // Component-unmount safety net — revoke any URL still live.
  useEffect(() => () => {
    if (blobRef.current) URL.revokeObjectURL(blobRef.current);
  }, []);

  const toggleExpanded = (groupKey) =>
    setExpanded((prev) => {
      const next = new Set(prev);
      if (next.has(groupKey)) next.delete(groupKey); else next.add(groupKey);
      return next;
    });

  const toggleSubRow = (id) =>
    setSubSelected((prev) => {
      const next = new Set(prev);
      if (next.has(id)) next.delete(id); else next.add(id);
      return next;
    });

  // Toggle a whole group: if fully selected → clear; else → select all
  // (which also clears any partial sub-picks for this group so state
  // is consistent).
  const toggleGroup = (it) => {
    const isFull = selected.has(it.group_key);
    setSelected((prev) => {
      const next = new Set(prev);
      if (isFull) next.delete(it.group_key); else next.add(it.group_key);
      return next;
    });
    if (!isFull) {
      // Selecting the whole group clears any leftover subrow picks for it.
      setSubSelected((prev) => {
        const next = new Set(prev);
        for (const c of (it.charges || [])) next.delete(c.id);
        return next;
      });
    }
  };

  // How many sub-rows of this group are currently checked (either via
  // whole-group selection or individual subrow picks)?  Drives the
  // group checkbox's tri-state.
  const groupSelectionState = (it) => {
    if (selected.has(it.group_key)) return "all";
    const total = (it.charges || []).length;
    if (total === 0) return "none";
    let n = 0;
    for (const c of it.charges) if (subSelected.has(c.id)) n += 1;
    if (n === 0) return "none";
    if (n === total) return "all";
    return "partial";
  };

  // Bulk button counter — number of allocations that will flip.
  const bulkCount = flattenSelectedIds().length;

  const clearAllSelection = () => {
    setSelected(new Set());
    setSubSelected(new Set());
  };

  // ---- actions ----
  const openMark = (id) => {
    setMarkTarget(id);
    setMarkForm({ external_ref: "", note: "" });
    setMarkOpen(true);
  };

  const submitMark = async () => {
    setBusy(true);
    try {
      if (markTarget === "bulk") {
        const ids = flattenSelectedIds();
        await api.post("/lessee-charges/bulk-mark", {
          ids, method: "manual", note: markForm.note || null,
        });
        clearAllSelection();
      } else {
        await api.post(`/expenses/${markTarget}/mark-lessee-charged`, {
          method: "manual",
          external_ref: markForm.external_ref || null,
          note: markForm.note || null,
        });
      }
      setMarkOpen(false);
      await load();
    } catch (e) {
      setError(extractError(e));
    } finally { setBusy(false); }
  };

  const unmark = async (id) => {
    setBusy(true);
    try {
      await api.post(`/expenses/${id}/unmark-lessee-charged`);
      await load();
    } catch (e) { setError(extractError(e)); }
    finally { setBusy(false); }
  };

  // CSV export
  const exportCsv = async () => {
    setExporting(true);
    try {
      const token = localStorage.getItem(TOKEN_KEY);
      const csvUrl = `${API_BASE}/lessee-charges/export.csv?status=${encodeURIComponent(filter)}`;
      const res = await fetch(csvUrl, {
        headers: token ? { Authorization: `Bearer ${token}` } : {},
      });
      if (!res.ok) throw new Error(`Export failed (${res.status})`);
      const blob = await res.blob();
      const url = URL.createObjectURL(blob);
      const a = document.createElement("a");
      a.href = url;
      const cd = res.headers.get("Content-Disposition") || "";
      const m = cd.match(/filename="?([^"]+)"?/i);
      a.download = m ? m[1] : `lessee-charges-${new Date().toISOString().slice(0, 10)}.csv`;
      document.body.appendChild(a);
      a.click(); a.remove();
      URL.revokeObjectURL(url);
    } catch (e) { setError(e.message || "Export failed"); }
    finally { setExporting(false); }
  };

  return (
    <>
      <Card className="mt-6 border-slate-200" data-testid="lessee-charges-card">
        <CardContent className="p-0">
          <div className="flex flex-wrap items-center justify-between gap-3 border-b border-slate-100 px-4 py-3">
            <div className="flex items-center gap-2">
              <Building2 className="h-4 w-4 text-[#0F1E3D]" />
              <h2 className="text-base font-semibold text-slate-900">Lessee Charges</h2>
              {total > 0 && (
                <span className="rounded-full bg-amber-100 px-2 py-0.5 text-xs font-medium text-amber-800"
                  data-testid="lc-count-badge">{total}</span>
              )}
            </div>
            <div className="flex items-center gap-2">
              {selected.size > 0 && (
                <Button size="sm"
                  onClick={() => { setMarkTarget("bulk"); setMarkForm({ external_ref: "", note: "" }); setMarkOpen(true); }}
                  className="bg-[#0F1E3D] text-white hover:bg-[#0F1E3D]/90"
                  data-testid="lc-bulk-mark-btn">
                  <CheckCircle2 className="mr-1 h-3 w-3" /> Mark {bulkCount || selected.size} as charged
                </Button>
              )}
              {selected.size === 0 && subSelected.size > 0 && (
                <Button size="sm"
                  onClick={() => { setMarkTarget("bulk"); setMarkForm({ external_ref: "", note: "" }); setMarkOpen(true); }}
                  className="bg-[#0F1E3D] text-white hover:bg-[#0F1E3D]/90"
                  data-testid="lc-bulk-mark-btn">
                  <CheckCircle2 className="mr-1 h-3 w-3" /> Mark {subSelected.size} selected as charged
                </Button>
              )}
              <Button size="sm" variant="ghost" onClick={load} disabled={loading}
                data-testid="lc-refresh-btn">
                {loading ? <Loader2 className="h-3 w-3 animate-spin" /> : "Refresh"}
              </Button>
              <Button size="sm" variant="outline" onClick={exportCsv}
                disabled={exporting || !items.length}
                data-testid="lc-export-csv-btn">
                <Download className="mr-1 h-3 w-3" />
                {exporting ? "Exporting…" : "Export CSV"}
              </Button>
            </div>
          </div>

          {error && (
            <div className="px-4 pt-3">
              <Alert variant="destructive"><AlertDescription>{error}</AlertDescription></Alert>
            </div>
          )}

          {/* Status tabs — mirrors DriverCharges.jsx (lines 547-563). */}
          <div className="flex flex-wrap items-center gap-2 px-4 py-3"
            data-testid="lc-status-tabs">
            {STATUS_TABS.map((t) => (
              <button
                key={t.key}
                type="button"
                onClick={() => setTab(t.key)}
                data-testid={`lc-tab-${t.key}`}
                className={`rounded-full px-3 py-1 text-sm font-medium ring-1 transition-colors ${
                  tab === t.key
                    ? "bg-[#0F1E3D] text-white ring-[#0F1E3D]"
                    : "bg-white text-slate-700 ring-slate-200 hover:bg-slate-50"
                }`}>
                {t.label}
              </button>
            ))}
          </div>

          <div className="overflow-x-auto">
            <table className="w-full text-left text-sm" data-testid="lc-table">
              <thead className="bg-slate-50 text-xs uppercase tracking-wide text-slate-500">
                <tr>
                  <th className="px-2 py-2 w-6"></th>{/* Phase 3 — chevron */}
                  <th className="px-3 py-2 w-8">
                    <Checkbox
                      checked={selected.size > 0 && selected.size === allEligibleIds.length && allEligibleIds.length > 0}
                      onCheckedChange={selectAllOnPage}
                      data-testid="lc-select-all" />
                  </th>
                  <th className="px-3 py-2">Date</th>
                  <th className="px-3 py-2">Vendor</th>
                  <th className="px-3 py-2 text-right">Amount</th>
                  <th className="px-3 py-2">Worker</th>
                  <th className="px-3 py-2">Lessee</th>
                  <th className="px-3 py-2">Truck(s)</th>
                  <th className="px-3 py-2">Trailer(s)</th>
                  <th className="px-3 py-2">Status</th>
                  <th className="px-3 py-2">Charged at</th>
                  <th className="px-3 py-2 text-right">Actions</th>
                </tr>
              </thead>
              <tbody>
                {loading ? (
                  <tr><td colSpan={12} className="px-3 py-6 text-center text-slate-500">
                    <Loader2 className="mx-auto h-4 w-4 animate-spin" /> Loading…
                  </td></tr>
                ) : items.length === 0 ? (
                  <tr><td colSpan={12} className="px-3 py-10 text-center text-slate-500" data-testid="lc-empty-state">
                    No lessee charges yet — workers can mark expenses with linked equipment as charged to a lessee.
                  </td></tr>
                ) : items.map((it) => {
                  const status = it.lessee_charge_status || "pending";
                  // ---------- Phase 3 — grouped row branch ----------
                  if (it.is_group) {
                    const gk = it.group_key;
                    const isExpanded = expanded.has(gk);
                    const gState = groupSelectionState(it);
                    const trackedIds = it.charges || [];
                    return (
                      <Fragment key={gk}>
                        <tr className="border-t border-slate-100 hover:bg-slate-50"
                          data-testid={`lc-group-row-${gk}`}>
                          <td className="px-2 py-2 w-6">
                            <button
                              type="button"
                              onClick={() => toggleExpanded(gk)}
                              aria-expanded={isExpanded}
                              aria-label={isExpanded ? "Collapse group" : "Expand group"}
                              className="inline-flex items-center justify-center rounded p-0.5 text-slate-500 hover:bg-slate-100 hover:text-slate-800"
                              data-testid={`lc-group-chevron-${gk}`}>
                              {isExpanded
                                ? <ChevronDown className="h-4 w-4" />
                                : <ChevronRight className="h-4 w-4" />}
                            </button>
                          </td>
                          <td className="px-3 py-2">
                            <Checkbox
                              checked={gState === "all"}
                              indeterminate={gState === "partial" || undefined}
                              onCheckedChange={() => toggleGroup(it)}
                              disabled={status !== "pending"}
                              aria-checked={gState === "partial" ? "mixed" : gState === "all"}
                              data-testid={`lc-group-select-${gk}`} />
                          </td>
                          <td className="px-3 py-2 whitespace-nowrap">{fmtDate(it.occurred_at)}</td>
                          <td className="px-3 py-2">
                            <span className="font-medium text-slate-900">{it.vendor}</span>
                            {it.source_ref?.invoice_number && (
                              <span className="ml-1 text-xs text-slate-500">
                                · #{it.source_ref.invoice_number}
                              </span>
                            )}
                          </td>
                          <td className="px-3 py-2 text-right font-medium">
                            {fmtMoney(it.amount_cents)}
                          </td>
                          <td className="px-3 py-2 text-slate-600">
                            {it.user_name === "Multiple" ? (
                              <span className="italic text-slate-400"
                                data-testid={`lc-group-worker-multiple-${gk}`}>
                                Multiple
                              </span>
                            ) : (it.user_name || "—")}
                          </td>
                          <td className="px-3 py-2" data-testid={`lc-group-lessee-${gk}`}>
                            <span className="inline-flex items-center gap-1 rounded-md bg-amber-50 px-1.5 py-0.5 text-xs font-medium text-amber-800 ring-1 ring-amber-200">
                              {it.lessee_name || "—"}
                            </span>
                          </td>
                          <td className="px-3 py-2"><EquipmentChips rows={it.trucks} /></td>
                          <td className="px-3 py-2"><EquipmentChips rows={it.trailers} /></td>
                          <td className="px-3 py-2"><StatusPill status={status} /></td>
                          <td className="px-3 py-2 whitespace-nowrap text-slate-600">{fmtDate(it.lessee_charged_at)}</td>
                          <td className="px-3 py-2 text-right">
                            <div className="flex justify-end items-center gap-1">
                              {status === "pending" && (
                                <Button size="sm" variant="ghost"
                                  onClick={() => {
                                    setMarkTarget("bulk");
                                    setMarkForm({ external_ref: "", note: "" });
                                    // If nothing else selected, this group becomes the single target.
                                    setSelected((prev) => {
                                      const next = new Set(prev);
                                      next.add(gk);
                                      return next;
                                    });
                                    setMarkOpen(true);
                                  }}
                                  data-testid={`lc-group-mark-btn-${gk}`}
                                  title="Mark whole group as charged">
                                  <CheckCircle2 className="h-4 w-4" />
                                </Button>
                              )}
                              <Button size="sm" variant="ghost"
                                onClick={() => openGroupDetails(gk)}
                                data-testid={`lc-group-view-${gk}`}
                                title="View details">
                                <ExternalLink className="h-4 w-4" />
                              </Button>
                            </div>
                          </td>
                        </tr>
                        {isExpanded && trackedIds.map((c) => {
                          const cStatus = c.lessee_charge_status || status;
                          return (
                            <tr key={`${gk}::${c.id}`}
                              className="border-t border-slate-100 bg-slate-50/60"
                              data-testid={`lc-subrow-${c.id}`}>
                              <td className="px-2 py-2 w-6"></td>
                              <td className="px-3 py-2">
                                <Checkbox
                                  checked={selected.has(gk) || subSelected.has(c.id)}
                                  disabled={selected.has(gk) || cStatus !== "pending"}
                                  onCheckedChange={() => toggleSubRow(c.id)}
                                  data-testid={`lc-subrow-select-${c.id}`} />
                              </td>
                              <td className="px-3 py-2 whitespace-nowrap text-xs text-slate-500">
                                {fmtDate(c.occurred_at)}
                              </td>
                              <td className="px-3 py-2 text-xs text-slate-600" colSpan={2}>
                                <span className="mr-2 rounded bg-slate-200 px-1.5 py-0.5 text-[10px] font-medium text-slate-700">
                                  part of group
                                </span>
                                <Link to={`/expenses/${c.id}`} className="text-slate-700 hover:underline">
                                  {c.description || "Repair charge"}
                                </Link>
                              </td>
                              <td className="px-3 py-2 text-right text-xs text-slate-700">
                                {fmtMoney(c.amount_cents)}
                              </td>
                              <td className="px-3 py-2 text-xs text-slate-500" colSpan={5}>
                                {c.user_name || "—"}
                              </td>
                              <td className="px-3 py-2 text-right">
                                <Link to={`/expenses/${c.id}`} title="View details">
                                  <Button size="sm" variant="ghost" data-testid={`lc-subrow-view-${c.id}`}>
                                    <ExternalLink className="h-3.5 w-3.5" />
                                  </Button>
                                </Link>
                              </td>
                            </tr>
                          );
                        })}
                      </Fragment>
                    );
                  }
                  // ---------- Existing flat-row branch (unchanged) ----------
                  return (
                  <tr key={it.id} className="border-t border-slate-100 hover:bg-slate-50"
                    data-testid={`lc-row-${it.id}`}>
                    <td className="px-2 py-2 w-6"></td>{/* chevron gutter for alignment */}
                    <td className="px-3 py-2">
                      <Checkbox
                        checked={selected.has(it.id)}
                        onCheckedChange={() => toggleRow(it.id)}
                        disabled={status !== "pending"}
                        data-testid={`lc-row-select-${it.id}`} />
                    </td>
                    <td className="px-3 py-2 whitespace-nowrap">{fmtDate(it.occurred_at)}</td>
                    <td className="px-3 py-2">
                      <Link to={`/expenses/${it.id}`} className="font-medium text-slate-900 hover:underline">{it.vendor}</Link>
                    </td>
                    <td className="px-3 py-2 text-right font-medium">{fmtMoney(it.amount_cents)}</td>
                    <td className="px-3 py-2 text-slate-600">{it.user_name || it.user_email || "—"}</td>
                    <td className="px-3 py-2" data-testid={`lc-row-lessee-${it.id}`}>
                      <span className="inline-flex items-center gap-1 rounded-md bg-amber-50 px-1.5 py-0.5 text-xs font-medium text-amber-800 ring-1 ring-amber-200">
                        {it.lessee_name || "—"}
                      </span>
                    </td>
                    <td className="px-3 py-2"><EquipmentChips rows={it.trucks} /></td>
                    <td className="px-3 py-2"><EquipmentChips rows={it.trailers} /></td>
                    <td className="px-3 py-2" data-testid={`lc-row-status-${it.id}`}><StatusPill status={status} /></td>
                    <td className="px-3 py-2 whitespace-nowrap text-slate-600">{fmtDate(it.lessee_charged_at)}</td>
                    <td className="px-3 py-2 text-right">
                      <div className="flex justify-end gap-1">
                        {status === "pending" && (
                          <Button size="sm" variant="ghost" onClick={() => openMark(it.id)}
                            data-testid={`lc-mark-btn-${it.id}`} title="Mark as charged">
                            <CheckCircle2 className="h-4 w-4" />
                          </Button>
                        )}
                        {(status === "charged" || status === "failed") && (
                          <Button size="sm" variant="ghost" onClick={() => unmark(it.id)}
                            disabled={busy}
                            data-testid={`lc-unmark-btn-${it.id}`} title="Unmark / revert to pending">
                            <RotateCcw className="h-4 w-4" />
                          </Button>
                        )}
                        <Link to={`/expenses/${it.id}`} title="View details">
                          <Button size="sm" variant="ghost" data-testid={`lc-view-${it.id}`}>
                            <ExternalLink className="h-4 w-4" />
                          </Button>
                        </Link>
                      </div>
                      {it.lessee_charge_external_ref && (
                        <p className="mt-1 text-right text-[10px] text-slate-500" title={it.lessee_charge_external_ref}>
                          ref: {it.lessee_charge_external_ref.slice(0, 24)}
                        </p>
                      )}
                    </td>
                  </tr>
                  );
                })}
              </tbody>
            </table>
          </div>
        </CardContent>
      </Card>

      {/* Audit panel — collapsible. Mirrors DriverCharges audit panel layout
          (DriverCharges.jsx lines 400-447) but lessee-only: terminal state is
          `lessee_charge_status == "charged"`, no Alvys ref/re-verify. */}
      <Card className="mt-4 border-slate-200" data-testid="lc-audit-panel">
        <CardContent className="p-3">
          <div className="flex flex-wrap items-center gap-2">
            <button
              type="button"
              onClick={() => setAuditOpen((v) => !v)}
              className="inline-flex items-center gap-2 text-sm font-semibold text-slate-800"
              data-testid="lc-audit-toggle">
              <FileSearch className="h-4 w-4" />
              Last {auditDays} days marked charged
              {auditOpen ? <ChevronUp className="h-4 w-4" /> : <ChevronDown className="h-4 w-4" />}
            </button>
            {auditOpen && (
              <div className="ml-auto flex items-center gap-2">
                <Label className="text-xs text-slate-500">Window</Label>
                {[7, 30, 90].map((n) => (
                  <button
                    key={n}
                    type="button"
                    onClick={() => setAuditDays(n)}
                    data-testid={`lc-audit-days-${n}`}
                    className={`rounded-full px-2.5 py-0.5 text-xs font-medium ring-1 ${
                      auditDays === n
                        ? "bg-[#0F1E3D] text-white ring-[#0F1E3D]"
                        : "bg-white text-slate-700 ring-slate-200 hover:bg-slate-50"
                    }`}>
                    {n}d
                  </button>
                ))}
                <Button size="sm" variant="outline" onClick={loadAudit}
                  disabled={auditLoading} data-testid="lc-audit-refresh">
                  {auditLoading ? <Loader2 className="h-3 w-3 animate-spin" /> : "Refresh"}
                </Button>
              </div>
            )}
          </div>

          {auditOpen && (
            <div className="mt-3">
              {auditError && (
                <Alert variant="destructive" className="mb-2"><AlertDescription>{auditError}</AlertDescription></Alert>
              )}
              <div className="overflow-x-auto">
                <table className="w-full text-left text-sm" data-testid="lc-audit-table">
                  <thead className="bg-slate-50 text-xs uppercase tracking-wide text-slate-500">
                    <tr>
                      <th className="px-2 py-2">Occurred</th>
                      <th className="px-2 py-2">Vendor</th>
                      <th className="px-2 py-2">Lessee</th>
                      <th className="px-2 py-2 text-right">Amount</th>
                      <th className="px-2 py-2">Truck(s)</th>
                      <th className="px-2 py-2">Trailer(s)</th>
                      <th className="px-2 py-2">Charged at</th>
                      <th className="px-2 py-2">Charged by</th>
                      <th className="px-2 py-2">Ref</th>
                      <th className="px-2 py-2">Method</th>
                    </tr>
                  </thead>
                  <tbody>
                    {auditLoading ? (
                      <tr><td colSpan={10} className="px-2 py-6 text-center text-slate-500">
                        <Loader2 className="mx-auto h-4 w-4 animate-spin" /> Loading…
                      </td></tr>
                    ) : auditRows.length === 0 ? (
                      <tr><td colSpan={10} className="px-2 py-6 text-center text-slate-500"
                        data-testid="lc-audit-empty">
                        No lessee charges marked in the last {auditDays} days.
                      </td></tr>
                    ) : auditRows.map((r) => (
                      <tr key={r.expense_id} className="border-t border-slate-100"
                        data-testid={`lc-audit-row-${r.expense_id}`}>
                        <td className="px-2 py-2 whitespace-nowrap">{fmtDate(r.occurred_at)}</td>
                        <td className="px-2 py-2">
                          <Link to={`/expenses/${r.expense_id}`} className="text-slate-900 hover:underline">{r.vendor}</Link>
                        </td>
                        <td className="px-2 py-2">
                          <span className="inline-flex rounded-md bg-amber-50 px-1.5 py-0.5 text-xs font-medium text-amber-800 ring-1 ring-amber-200">
                            {r.lessee_name || "—"}
                          </span>
                        </td>
                        <td className="px-2 py-2 text-right font-medium">{fmtMoney(r.amount_cents)}</td>
                        <td className="px-2 py-2 text-slate-600">{r.trucks_label || "—"}</td>
                        <td className="px-2 py-2 text-slate-600">{r.trailers_label || "—"}</td>
                        <td className="px-2 py-2 whitespace-nowrap text-slate-600">{fmtDate(r.charged_at)}</td>
                        <td className="px-2 py-2 text-slate-600">{r.charged_by_label || "—"}</td>
                        <td className="px-2 py-2 text-slate-500 font-mono text-xs">{r.external_ref || "—"}</td>
                        <td className="px-2 py-2 text-slate-500 text-xs">{r.method || "—"}</td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            </div>
          )}
        </CardContent>
      </Card>

      {/* Mark dialog (single + bulk) */}
      <Dialog open={markOpen} onOpenChange={setMarkOpen}>
        <DialogContent data-testid="lc-mark-dialog">
          <DialogHeader>
            <DialogTitle>
              <Banknote className="mr-1 inline h-4 w-4 -translate-y-0.5" />
              {markTarget === "bulk" ? `Mark ${bulkCount || selected.size} as charged` : "Mark as charged"}
            </DialogTitle>
            <DialogDescription>
              Records the lessee chargeback as completed in our books. Optionally add an
              invoice/reference id and an internal note.
            </DialogDescription>
          </DialogHeader>
          {markTarget !== "bulk" && (
            <div className="space-y-2">
              <Label>External reference</Label>
              <Input
                value={markForm.external_ref}
                onChange={(e) => setMarkForm((f) => ({ ...f, external_ref: e.target.value }))}
                placeholder="Optional (e.g. invoice no.)"
                data-testid="lc-mark-external-ref" />
            </div>
          )}
          <div className="space-y-2">
            <Label>Note</Label>
            <Textarea
              value={markForm.note}
              onChange={(e) => setMarkForm((f) => ({ ...f, note: e.target.value }))}
              placeholder="Optional"
              rows={3}
              data-testid="lc-mark-note" />
          </div>
          <DialogFooter>
            <Button variant="ghost" onClick={() => setMarkOpen(false)} disabled={busy}>Cancel</Button>
            <Button onClick={submitMark} disabled={busy}
              className="bg-[#0F1E3D] text-white hover:bg-[#0F1E3D]/90"
              data-testid="lc-mark-submit-btn">
              {busy ? "Marking…" : "Confirm"}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Grouped-row details modal — mirrors ExpenseDetail's layout so
          accounting sees the same visual for grouped and flat rows.
          Toll/EFS flat rows still route to /expenses/{id} (unchanged).
          Aggregated fields render "Multiple" when the group's
          constituents disagree; the RECEIPT card renders the lessee
          statement PDF exactly like a toll receipt (icon + filename +
          PDF · size + external-link). */}
      <Dialog open={detailsOpen} onOpenChange={setDetailsOpen}>
        <DialogContent className="max-w-3xl" data-testid="lc-group-details-dialog">
          {detailsLoading || !detailsGroup ? (
            <div className="flex items-center justify-center py-10 text-slate-500"
              data-testid="lc-group-details-loading">
              {detailsError
                ? <Alert variant="destructive"><AlertDescription>{detailsError}</AlertDescription></Alert>
                : <><Loader2 className="mr-2 h-4 w-4 animate-spin" /> Loading…</>}
            </div>
          ) : (
            <>
              <DialogHeader className="space-y-3">
                <div className="flex items-start justify-between gap-3">
                  <div>
                    <DialogTitle className="text-2xl font-semibold text-slate-900">
                      {detailsGroup.vendor || "—"}
                      {detailsGroup.invoice_number && (
                        <span className="ml-2 text-base font-normal text-slate-500">
                          · #{detailsGroup.invoice_number}
                        </span>
                      )}
                    </DialogTitle>
                    <DialogDescription className="mt-1 text-sm text-slate-500">
                      {fmtDateTime(detailsGroup.date)}
                    </DialogDescription>
                  </div>
                  <div className="text-right">
                    <p className="text-3xl font-bold text-slate-900"
                      data-testid="lc-group-details-amount">
                      {fmtMoney(detailsGroup.amount_cents)}
                    </p>
                    <div className="mt-1 flex justify-end">
                      <StatusPill status={detailsGroup.status} />
                    </div>
                  </div>
                </div>
              </DialogHeader>

              <div className="space-y-4 pt-2">
                <dl className="grid grid-cols-1 gap-x-6 gap-y-3 text-sm sm:grid-cols-2">
                  {[
                    { key: "card", label: "Card", testid: "lc-group-details-card" },
                    { key: "category", label: "Category", testid: "lc-group-details-category" },
                    { key: "company", label: "Company", testid: "lc-group-details-company" },
                    { key: "department", label: "Department", testid: "lc-group-details-department" },
                  ].map(({ key, label, testid }) => {
                    const f = detailsGroup[key] || {};
                    return (
                      <div key={key}>
                        <dt className="text-xs uppercase tracking-wide text-slate-500">{label}</dt>
                        <dd className="mt-1 text-slate-900" data-testid={testid}>
                          {f.multiple
                            ? <span className="italic text-slate-400">Multiple</span>
                            : (f.value || <span className="text-slate-400">—</span>)}
                        </dd>
                      </div>
                    );
                  })}

                  <div className="sm:col-span-2">
                    <dt className="text-xs uppercase tracking-wide text-slate-500">TMS links</dt>
                    <dd className="mt-1 grid grid-cols-1 gap-3 sm:grid-cols-3"
                      data-testid="lc-group-details-tms">
                      {[
                        { key: "trucks", label: "Trucks", icon: Truck, items: detailsGroup.trucks || [] },
                        { key: "trailers", label: "Trailers", icon: Container, items: detailsGroup.trailers || [] },
                        { key: "drivers", label: "Drivers", icon: UserIcon, items: detailsGroup.drivers || [] },
                      ].map((g) => {
                        const Icon = g.icon;
                        return (
                          <div key={g.key} data-testid={`lc-group-details-tms-${g.key}`}>
                            <p className="text-xs font-medium text-slate-600">{g.label}</p>
                            {g.items.length === 0 ? (
                              <p className="mt-1 text-sm text-slate-400">None</p>
                            ) : (
                              <ul className="mt-1 space-y-1">
                                {g.items.map((it) => (
                                  <li key={it.id}
                                    className="inline-flex max-w-full items-center gap-1.5 rounded-full bg-[#0F1E3D]/5 px-2 py-1 text-xs text-[#0F1E3D] ring-1 ring-[#0F1E3D]/15">
                                    <Icon className="h-3 w-3" />
                                    <span className="truncate">{it.label || it.id}</span>
                                  </li>
                                ))}
                              </ul>
                            )}
                          </div>
                        );
                      })}
                    </dd>
                  </div>

                  <div>
                    <dt className="text-xs uppercase tracking-wide text-slate-500">Charge driver</dt>
                    <dd className="mt-1" data-testid="lc-group-details-charge-driver">
                      {detailsGroup.charge_driver?.multiple
                        ? <span className="italic text-slate-400">Multiple</span>
                        : detailsGroup.charge_driver?.value
                          ? (
                            <span className="inline-flex items-center gap-1 rounded-full bg-amber-50 px-2 py-0.5 text-xs font-medium text-amber-700">
                              <AlertTriangle className="h-3 w-3" /> Charged back to driver
                            </span>
                          )
                          : <span className="text-sm text-slate-400">No</span>}
                    </dd>
                  </div>

                  <div>
                    <dt className="text-xs uppercase tracking-wide text-slate-500">Worker</dt>
                    <dd className="mt-1 text-slate-900" data-testid="lc-group-details-worker">
                      {detailsGroup.worker?.multiple
                        ? <span className="italic text-slate-400">Multiple</span>
                        : (detailsGroup.worker?.value || <span className="text-slate-400">—</span>)}
                    </dd>
                  </div>

                  <div className="sm:col-span-2">
                    <dt className="text-xs uppercase tracking-wide text-slate-500">Comment</dt>
                    <dd className="mt-1 whitespace-pre-wrap text-slate-900"
                      data-testid="lc-group-details-comment">
                      {detailsGroup.comment?.multiple
                        ? <span className="italic text-slate-400">Multiple comments — expand line items to view</span>
                        : (detailsGroup.comment?.value
                          || <span className="text-slate-400">No comment</span>)}
                    </dd>
                  </div>

                  <div className="sm:col-span-2">
                    <dt className="text-xs uppercase tracking-wide text-slate-500">Receipt</dt>
                    <dd className="mt-1">
                      {detailsGroup.receipt ? (
                        // Server has the PDF — three sub-states on the
                        // client blob fetch: error, loading, ready.
                        receiptBlob.error ? (
                          <div className="max-w-sm">
                            <div className="flex items-center gap-3 rounded-md border border-amber-200 bg-amber-50 p-3"
                              data-testid="lc-group-details-receipt-blob-error">
                              <AlertTriangle className="h-8 w-8 shrink-0 text-amber-600" />
                              <div className="min-w-0 flex-1">
                                <p className="truncate text-sm font-medium text-amber-900"
                                  title={detailsGroup.receipt.filename}>
                                  Failed to load PDF
                                </p>
                                <p className="truncate text-xs text-amber-700"
                                  title={receiptBlob.error}>
                                  {receiptBlob.error} — try Retry
                                </p>
                              </div>
                              <Button size="sm" variant="outline"
                                onClick={retryReceiptBlob}
                                disabled={receiptBlob.loading}
                                className="border-amber-300 text-amber-800 hover:bg-amber-100"
                                data-testid="lc-group-details-receipt-blob-retry">
                                {receiptBlob.loading
                                  ? <Loader2 className="h-3 w-3 animate-spin" />
                                  : "Retry"}
                              </Button>
                            </div>
                          </div>
                        ) : !receiptBlob.url ? (
                          <div className="max-w-sm">
                            <div className="flex items-center gap-3 rounded-md border border-slate-200 bg-slate-50 p-3"
                              data-testid="lc-group-details-receipt-blob-loading">
                              <FileText className="h-10 w-10 shrink-0 text-rose-600 opacity-60" />
                              <div className="min-w-0 flex-1">
                                <p className="truncate text-sm font-medium text-slate-900"
                                  title={detailsGroup.receipt.filename}>
                                  {detailsGroup.receipt.filename}
                                </p>
                                <p className="text-xs text-slate-500">
                                  PDF{detailsGroup.receipt.bytes_size
                                    ? ` · ${fmtSize(detailsGroup.receipt.bytes_size)}` : ""}
                                </p>
                              </div>
                              <Loader2 className="h-4 w-4 shrink-0 animate-spin text-slate-400" />
                            </div>
                            <p className="mt-2 text-xs italic text-slate-500"
                              data-testid="lc-group-details-receipt-source">
                              Lessee statement · document {String(detailsKey || "").slice(-8)}
                            </p>
                          </div>
                        ) : (
                          <div className="max-w-sm">
                            <a
                              href={receiptBlob.url}
                              target="_blank"
                              rel="noopener noreferrer"
                              className="flex items-center gap-3 rounded-md border border-slate-200 bg-slate-50 p-3 hover:border-slate-300 hover:bg-slate-100"
                              data-testid="lc-group-details-receipt">
                              <FileText className="h-10 w-10 shrink-0 text-rose-600" />
                              <div className="min-w-0 flex-1">
                                <p className="truncate text-sm font-medium text-slate-900"
                                  title={detailsGroup.receipt.filename}>
                                  {detailsGroup.receipt.filename}
                                </p>
                                <p className="text-xs text-slate-500">
                                  PDF{detailsGroup.receipt.bytes_size
                                    ? ` · ${fmtSize(detailsGroup.receipt.bytes_size)}` : ""}
                                </p>
                              </div>
                              <ExternalLink className="h-4 w-4 shrink-0 text-slate-400" />
                            </a>
                            <p className="mt-2 text-xs italic text-slate-500"
                              data-testid="lc-group-details-receipt-source">
                              Lessee statement · document {String(detailsKey || "").slice(-8)}
                            </p>
                          </div>
                        )
                      ) : detailsGroup.receipt_pending ? (
                        <div className="max-w-sm">
                          <div className="flex items-center gap-3 rounded-md border border-dashed border-slate-200 bg-slate-50 p-3"
                            title="Preparing statement…"
                            data-testid="lc-group-details-receipt-pending">
                            <div className="h-10 w-10 shrink-0 animate-pulse rounded bg-slate-200" />
                            <div className="min-w-0 flex-1 space-y-1.5">
                              <div className="h-3 w-3/4 animate-pulse rounded bg-slate-200" />
                              <div className="h-3 w-1/3 animate-pulse rounded bg-slate-200" />
                            </div>
                            <Loader2 className="h-4 w-4 shrink-0 animate-spin text-slate-400" />
                          </div>
                          <p className="mt-2 text-xs italic text-slate-500">
                            Preparing statement…
                          </p>
                        </div>
                      ) : detailsGroup.receipt_last_error ? (
                        <div className="max-w-sm">
                          <div className="flex items-center gap-3 rounded-md border border-amber-200 bg-amber-50 p-3"
                            data-testid="lc-group-details-receipt-error">
                            <AlertTriangle className="h-8 w-8 shrink-0 text-amber-600" />
                            <div className="min-w-0 flex-1">
                              <p className="truncate text-sm font-medium text-amber-900"
                                title={detailsGroup.receipt_last_error}>
                                Statement render failed
                              </p>
                              <p className="truncate text-xs text-amber-700"
                                title={detailsGroup.receipt_last_error}>
                                {detailsGroup.receipt_last_error}
                              </p>
                            </div>
                            <Button size="sm" variant="outline"
                              onClick={retryDetailsReceipt}
                              disabled={retryingReceipt.has(detailsKey)}
                              className="border-amber-300 text-amber-800 hover:bg-amber-100"
                              data-testid="lc-group-details-receipt-retry">
                              {retryingReceipt.has(detailsKey)
                                ? <Loader2 className="h-3 w-3 animate-spin" />
                                : "Retry"}
                            </Button>
                          </div>
                        </div>
                      ) : (
                        <p className="text-sm text-slate-400">No receipt</p>
                      )}
                    </dd>
                  </div>
                </dl>

                <p className="text-xs text-slate-400" data-testid="lc-group-details-logged">
                  Logged {fmtDateTime(detailsGroup.logged_at)}
                  {detailsGroup.charged_at
                    ? ` · charged ${fmtDateTime(detailsGroup.charged_at)}` : ""}
                </p>

                {/* Line items (N) — expandable, mirrors the list-page
                    sub-row shape so an accountant can audit every
                    allocation without leaving the modal. */}
                <div className="border-t border-slate-100 pt-3">
                  <button
                    type="button"
                    onClick={() => setDetailsLineItemsOpen((v) => !v)}
                    className="inline-flex items-center gap-1 text-sm font-medium text-slate-700 hover:text-slate-900"
                    data-testid="lc-group-details-lineitems-toggle">
                    {detailsLineItemsOpen
                      ? <ChevronDown className="h-4 w-4" />
                      : <ChevronRight className="h-4 w-4" />}
                    Line items ({detailsGroup.count})
                  </button>
                  {detailsLineItemsOpen && (
                    <div className="mt-2 overflow-x-auto">
                      <table className="w-full text-left text-sm"
                        data-testid="lc-group-details-lineitems-table">
                        <thead className="bg-slate-50 text-xs uppercase tracking-wide text-slate-500">
                          <tr>
                            <th className="px-2 py-1.5">Date</th>
                            <th className="px-2 py-1.5">Description</th>
                            <th className="px-2 py-1.5 text-right">Amount</th>
                          </tr>
                        </thead>
                        <tbody>
                          {(detailsGroup.charges || []).map((c) => (
                            <tr key={c.id} className="border-t border-slate-100"
                              data-testid={`lc-group-details-lineitem-${c.id}`}>
                              <td className="px-2 py-1.5 whitespace-nowrap text-slate-600">
                                {fmtDate(c.occurred_at)}
                              </td>
                              <td className="px-2 py-1.5 text-slate-700">
                                {c.description || <span className="italic text-slate-400">Repair charge</span>}
                              </td>
                              <td className="px-2 py-1.5 text-right font-medium text-slate-800">
                                {fmtMoney(c.amount_cents)}
                              </td>
                            </tr>
                          ))}
                        </tbody>
                      </table>
                    </div>
                  )}
                </div>
              </div>
            </>
          )}
        </DialogContent>
      </Dialog>
    </>
  );
}
